const messageModule = {
  namespaced: true,

  state: {
    // Map of room messages: { roomId: [message1, message2, ...] }
    roomMessages: {},

    // Map of sent messages: { roomId: { messageId: message, messageId2: message2, ... } }
    sentMessages: {},

    // Map of total message counts per room: { roomId: total }
    roomMessageTotals: {}
  },
  
  getters: {
    // Get messages for a specific room
    getRoomMessages: (state) => (roomId) => {
      return state.roomMessages[roomId] || [];
    },

    // Get sent messages for a specific room
    getSentMessages: (state) => (roomId) => {
      return state.sentMessages[roomId] || {};
    },

    // Get a specific sent message by room and message ID
    getSentMessage: (state) => (roomId, messageId) => {
      return state.sentMessages[roomId]?.[messageId] || null;
    },

    // Get all rooms that have messages
    getRoomsWithMessages: (state) => {
      return Object.keys(state.roomMessages);
    },

    // Get message count for a specific room (currently loaded)
    getMessageCount: (state) => (roomId) => {
      return state.roomMessages[roomId]?.length || 0;
    },

    // Get total message count for a specific room
    getTotalMessageCount: (state) => (roomId) => {
      return state.roomMessageTotals[roomId] || 0;
    },

    // Check if there are more messages to load
    hasMoreMessages: (state, getters) => (roomId) => {
      const current = getters.getMessageCount(roomId);
      const total = state.roomMessageTotals[roomId] || 0;
      return current < total;
    }
  },
  
  mutations: {
    // Initialize messages for a room (sets empty array if not exists or sets provided messages)
    INIT_ROOM_MESSAGES(state, { roomId, messages = [] }) {
      // Initialize room messages
      state.roomMessages = {
        ...state.roomMessages,
        [roomId]: [...messages]
      };
      
      // Initialize sent messages for the room if not exists
      if (!state.sentMessages[roomId]) {
        state.sentMessages = {
          ...state.sentMessages,
          [roomId]: {}
        };
      }
    },
    
    // Add a single message to a room
    ADD_MESSAGE_TO_ROOM(state, { roomId, message }) {
      // Initialize room if it doesn't exist
      if (!state.roomMessages[roomId]) {
        state.roomMessages = {
          ...state.roomMessages,
          [roomId]: []
        };
      }
      
      // Push message to the room's message array
      state.roomMessages[roomId].push(message);
    },
    
    // Add multiple messages to a room
    ADD_MESSAGES_TO_ROOM(state, { roomId, messages }) {
      // Initialize room if it doesn't exist
      if (!state.roomMessages[roomId]) {
        state.roomMessages = {
          ...state.roomMessages,
          [roomId]: []
        };
      }
      
      // Push all messages to the room's message array
      state.roomMessages[roomId].push(...messages);
    },
    
    // Set all messages for a room (replaces existing)
    SET_ROOM_MESSAGES(state, { roomId, messages }) {
      state.roomMessages = {
        ...state.roomMessages,
        [roomId]: [...messages]
      };
    },
    
    // Add a sent message
    ADD_SENT_MESSAGE(state, { roomId, messageId, message }) {
      if (!state.sentMessages[roomId]) {
        state.sentMessages = {
          ...state.sentMessages,
          [roomId]: {}
        };
      }
      
      state.sentMessages[roomId] = {
        ...state.sentMessages[roomId],
        [messageId]: message
      };
    },
    
    // Remove a sent message
    REMOVE_SENT_MESSAGE(state, { roomId, messageId }) {
      if (state.sentMessages[roomId]) {
        const updatedMessages = { ...state.sentMessages[roomId] };
        delete updatedMessages[messageId];
        
        state.sentMessages = {
          ...state.sentMessages,
          [roomId]: updatedMessages
        };
      }
    },
    
    // Clear all messages for a room
    CLEAR_ROOM_MESSAGES(state, roomId) {
      if (state.roomMessages[roomId]) {
        state.roomMessages = {
          ...state.roomMessages,
          [roomId]: []
        };
      }
      
      if (state.sentMessages[roomId]) {
        state.sentMessages = {
          ...state.sentMessages,
          [roomId]: {}
        };
      }
    },
    
    // Clear all messages from all rooms
    CLEAR_ALL_MESSAGES(state) {
      state.roomMessages = {};
      state.sentMessages = {};
      state.roomMessageTotals = {};
    },

    // Set total message count for a room
    SET_ROOM_TOTAL(state, { roomId, total }) {
      state.roomMessageTotals = {
        ...state.roomMessageTotals,
        [roomId]: total
      };
    },

    // Increment total message count for a room
    INCREMENT_ROOM_TOTAL(state, roomId) {
      const currentTotal = state.roomMessageTotals[roomId] || 0;
      state.roomMessageTotals = {
        ...state.roomMessageTotals,
        [roomId]: currentTotal + 1
      };
    }
  },
  
  actions: {
    // Initialize a room for message storage (optionally with initial messages)
    initializeRoom({ commit }, { roomId, messages }) {
      commit('INIT_ROOM_MESSAGES', { roomId, messages });
    },
    
    // Add a message to a room
    addMessage({ commit }, { roomId, message }) {
      commit('ADD_MESSAGE_TO_ROOM', { roomId, message });
    },
    
    // Add multiple messages to a room
    addMessages({ commit }, { roomId, messages }) {
      commit('ADD_MESSAGES_TO_ROOM', { roomId, messages });
    },
    
    // Set all messages for a room
    setRoomMessages({ commit }, { roomId, messages }) {
      commit('SET_ROOM_MESSAGES', { roomId, messages });
    },
    
    // Add a sent message
    addSentMessage({ commit }, { roomId, messageId, message }) {
      commit('ADD_SENT_MESSAGE', { roomId, messageId, message });
    },
    
    // Remove a sent message
    removeSentMessage({ commit }, { roomId, messageId }) {
      commit('REMOVE_SENT_MESSAGE', { roomId, messageId });
    },
    
    // Clear messages for a room
    clearRoom({ commit }, roomId) {
      commit('CLEAR_ROOM_MESSAGES', roomId);
    },
    
    // Clear all messages
    clearAllMessages({ commit }) {
      commit('CLEAR_ALL_MESSAGES');
    },

    // Set total message count for a room
    setRoomTotal({ commit }, { roomId, total }) {
      commit('SET_ROOM_TOTAL', { roomId, total });
    },

    // Increment total message count (when new message arrives or is sent)
    incrementRoomTotal({ commit }, roomId) {
      commit('INCREMENT_ROOM_TOTAL', roomId);
    }
  }
};

export default messageModule;